import { useIsMutating, useQueryClient } from '@tanstack/react-query'
import { getQueryKey } from '@trpc/react-query'
import { addWeeks, endOfToday, isAfter, isBefore } from 'date-fns'
import { router } from 'expo-router'
import { useCallback, useEffect, useMemo } from 'react'

import MedicalInfoType from '@/enums/medicalInfoType'
import Species from '@/enums/species'
import { SubscriptionTier } from '@/store/organizationStore'
import { useAppStore } from '@/store/useAppStore'
import log from '@/utils/datadog/log/log'
import {
  getEntitledTier,
  resolveSubscriptionState,
} from '@/utils/purchases/billing'
import purchases from '@/utils/purchases/purchases'
import trpc from '@/utils/trpc'

const OrganizationService = {
  useCurrentOrganization: () => {
    const currentOrganization =
      useAppStore.use.currentOrganization().organization
    const setCurrentOrganization = useAppStore.use.setCurrentOrganization()

    const queryClient = useQueryClient()

    const organizationQuery = trpc.organization.byId.useQuery(
      {
        organizationId: currentOrganization?.id || '',
      },
      {
        enabled: !!currentOrganization?.id,
      }
    )

    const currentUserQuery = trpc.user.current.useQuery({
      organizationId: currentOrganization?.id,
    })

    // Memoize organization ID to prevent unnecessary re-renders
    const organizationId = useMemo(
      () => currentOrganization?.id,
      [currentOrganization?.id]
    )

    // Stabilize the setCurrentOrganization function
    const updateCurrentOrganization = useCallback(
      (update: Parameters<typeof setCurrentOrganization>[0]) => {
        setCurrentOrganization(update)
      },
      [setCurrentOrganization]
    )

    // Handle the case where the organization was deleted, and the user needs to be redirected to the home screen
    // This will trigger a re-render of the stack, which will trigger a new default organization to be set
    useEffect(() => {
      if (
        organizationId &&
        organizationQuery.failureReason?.data?.code === 'NOT_FOUND'
      ) {
        currentUserQuery.refetch()
        updateCurrentOrganization({ organization: null })
        router.dismissTo('/')
      }
    }, [
      organizationId, // ✅ Only depend on the ID, not the full object
      organizationQuery.failureReason,
      updateCurrentOrganization,
      organizationQuery,
      currentUserQuery,
    ])

    // Separate effect for organization data updates
    useEffect(() => {
      if (currentOrganization && organizationQuery.data) {
        const newOrgData = organizationQuery.data
        if (
          currentOrganization.id !== newOrgData.id ||
          currentOrganization.name !== newOrgData.name ||
          currentOrganization.logo !== newOrgData.logo ||
          currentOrganization.qrCode !== newOrgData.qrCode ||
          currentOrganization.adoptionContract !== newOrgData.adoptionContract
        ) {
          updateCurrentOrganization({
            organization: {
              ...currentOrganization,
              id: newOrgData.id,
              name: newOrgData.name,
              logo: newOrgData.logo,
              qrCode: newOrgData.qrCode,
              adoptionContract: newOrgData.adoptionContract,
            },
          })
        }
      }
    }, [currentOrganization, organizationQuery.data, updateCurrentOrganization])

    return {
      currentOrganization,
      refreshOrganization: () => {
        queryClient.invalidateQueries({
          queryKey: getQueryKey(trpc.organization.byId),
        })
        queryClient.invalidateQueries({
          queryKey: getQueryKey(trpc.user.current),
        })

        return organizationQuery.refetch()
      },
      organizationQueryData: organizationQuery.data,
    }
  },
  // Keeps the cached subscription state in sync with RevenueCat and the foster count.
  // Mount it once, in the organization layout.
  useSubscriptionStatus: () => {
    const organizationId = useAppStore.use.currentOrganization().organization?.id
    const setCurrentOrganization = useAppStore.use.setCurrentOrganization()

    const fosterCountQuery = trpc.foster.currentMonthFosterCount.useQuery(
      {
        organizationId: organizationId || '',
      },
      {
        enabled: !!organizationId,
      }
    )
    const fosterCount = fosterCountQuery.data?.count

    useEffect(() => {
      // Without the real count we can't tell whether the org is over its allotment
      if (!organizationId || fosterCount === undefined) {
        return
      }

      let cancelled = false

      async function checkSubscriptionStatus(orgId: string, count: number) {
        let entitlement: { tier?: SubscriptionTier } | undefined
        try {
          const customerInfo = await purchases.getCustomerInfo(orgId)
          if (customerInfo) {
            entitlement = {
              tier: getEntitledTier(customerInfo.entitlements.all),
            }
          }
        } catch (error) {
          log.warn('Could not read the subscription from RevenueCat', {
            error,
            organizationId: orgId,
          })
        }

        const latestOrg =
          useAppStore.getState().currentOrganization.organization
        if (cancelled || latestOrg?.id !== orgId) {
          return
        }

        const next = resolveSubscriptionState({
          cachedTier: latestOrg.subscriptionTier,
          entitlement,
          fosterCount: count,
        })
        if (
          next &&
          (next.subscriptionActive !== latestOrg.subscriptionActive ||
            next.subscriptionTier !== latestOrg.subscriptionTier)
        ) {
          setCurrentOrganization({ organization: { ...latestOrg, ...next } })
        }
      }

      checkSubscriptionStatus(organizationId, fosterCount)

      return () => {
        cancelled = true
      }
    }, [organizationId, fosterCount, setCurrentOrganization])
  },
  useIsSaving() {
    const mutationKey = getQueryKey(trpc.organization.update)
    return useIsMutating({ mutationKey }) > 0
  },
  useMedicalInfo: (
    type: MedicalInfoType,
    birthDate?: Date | null,
    species?: Species
  ) => {
    const currentOrganization =
      useAppStore.use.currentOrganization().organization

    const organization = trpc.organization.byId.useQuery(
      {
        organizationId: currentOrganization?.id || '',
      },
      {
        enabled: !!currentOrganization,
      }
    ).data

    if (!organization?.medicalInfos) {
      return []
    }

    const filteredMedicalInfos = organization.medicalInfos
      .filter(
        (m) =>
          m.type === type &&
          (!m.species || m.species === species) &&
          !!birthDate &&
          (!m.minAgeWeeks ||
            isAfter(endOfToday(), addWeeks(birthDate, m.minAgeWeeks || 0))) &&
          (!m.maxAgeWeeks ||
            isBefore(endOfToday(), addWeeks(birthDate, m.maxAgeWeeks || 0)))
      )
      .sort((a, b) => {
        if (a.minAgeWeeks && b.minAgeWeeks) {
          return a.minAgeWeeks - b.minAgeWeeks
        }

        if (a.minAgeWeeks && !b.minAgeWeeks) {
          return -1
        }

        if (!a.minAgeWeeks && b.minAgeWeeks) {
          return 1
        }

        return 0
      })

    return filteredMedicalInfos
  },
}

export default OrganizationService
